import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { systemLogsRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute } from 'src/app/app.selectors';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { CallDetailDialogComponent } from 'src/app/common-components/call-details-dialog/call-detail-dialog.component';
import { CallLogDialogComponent } from 'src/app/common-components/call-logs-dialog copy/call-log-dialog.component';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { SystemLogsFilterOption } from '../models/system-log-filter-option';
import { SystemLogOrderingOptions, SystemLogRegisterOptions } from '../models/system-log-register-options';
import { LogRegisterListItem, LogRegisterResults } from '../models/system-log-register-results.model';
import {
   GetSystemLogRegister,
   GetSystemLogRegister_systemLogsConnection_items,
} from '../queries/generated/GetSystemLogRegister';
import { SystemLogRegisterApiService } from '../services/system-log-register-api.service';
import { SystemLogRegisterSessionStorageService } from '../services/system-log-register-session-storage.service';
import * as systemLogActions from '../state/system-log-register.actions';
import { logRegisterAdapterActions } from '../state/system-log-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './system-log-register.reducer';
import * as SystemLogRegisterSelectors from './system-log-register.selectors';
import { selectFilters } from './system-log-register.selectors';

@Injectable()
export class LogRegisterEffects {
   loadSystemLogList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(logRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectCurrentRoute)),
            this.store.pipe(select(SystemLogRegisterSelectors.selectIsOnSystemPage))
         ),
         switchMap(([, filters, quickFilters, orderingOptions, route, wasPreviouslyOnSystemPage]) => {
            const sessionOrderingOptions = this.systemLogRegisterSessionStorageService.getSystemLogOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(systemLogActions.setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            const path = route.url[0].path;
            if (path === systemLogsRoute) {
               if (!wasPreviouslyOnSystemPage) {
                  this.store.dispatch(logRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(systemLogActions.loadSystemLogsSuccess());
            } else {
               if (wasPreviouslyOnSystemPage) {
                  this.store.dispatch(logRegisterAdapterActions.clearRegisterFilters());
                  quickFilters = null;
               }
               this.store.dispatch(systemLogActions.loadAccountLogsSuccess());
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadSystemLogPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(logRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(SystemLogRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, endCursor, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.systemLogRegisterSessionStorageService.getSystemLogOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(systemLogActions.setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(logRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(SystemLogRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(logRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            this.systemLogRegisterSessionStorageService.setSystemLogOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   openDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(systemLogActions.openDialog),
            withLatestFrom(this.store.pipe(select(getCurrentUserIsSystemUser))),
            tap(([action, isSystemUser]) => {
               this.dialog.open(CallDetailDialogComponent, {
                  data: { callLogs: action.callLogs, isSystemUser, canHangupCalls: false },
                  width: '800px',
                  autoFocus: false,
               });
            })
         );
      },
      { dispatch: false }
   );

   openLogDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(systemLogActions.openLogDialog),
            withLatestFrom(this.store.pipe(select(getCurrentUserIsSystemUser))),
            tap(([action, isSystemUser]) => {
               this.dialog.open(CallLogDialogComponent, {
                  data: { callLogs: action.callLogs, isSystemUser },
                  width: '700px',
               });
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private systemLogRegisterApiService: SystemLogRegisterApiService,
      private dialog: MatDialog,
      private systemLogRegisterSessionStorageService: SystemLogRegisterSessionStorageService
   ) {}

   private static getCallExtension(
      extension: { number?: number | undefined; name?: string | null },
      phoneNumber: string | null,
      isCallDestination = false
   ): string {
      if (phoneNumber && !isCallDestination && extension.number === undefined) {
         return phoneNumber;
      }

      let extensionDisplay = '';
      if (extension.number) {
         extensionDisplay += `${extension.number}`;
      }
      if (extension.name) {
         extensionDisplay += ` - ${extension.name}`;
      }
      if (phoneNumber && isCallDestination && extensionDisplay === '') {
         return phoneNumber;
      }
      return extensionDisplay;
   }

   private loadNewData(
      filters: SystemLogsFilterOption[],
      quickFilters: SystemLogRegisterOptions | null = null,
      orderingOptions: SystemLogOrderingOptions[] | null = null
   ) {
      return this.systemLogRegisterApiService.getSystemLog(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return logRegisterAdapterActions.loadSuccess(this.convertGetSystemLogResultToLoadSuccessProps(result.data));
         }),
         catchError((error) => {
            return of(logRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: SystemLogsFilterOption[],
      quickFilters: SystemLogRegisterOptions | null = null,
      orderingOptions: SystemLogOrderingOptions[] | null = null
   ) {
      return this.systemLogRegisterApiService.getSystemLog(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return logRegisterAdapterActions.loadPageSuccess(
               this.convertGetSystemLogResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(logRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetSystemLogResultToLoadSuccessProps(getSystemLog: GetSystemLogRegister): LogRegisterResults {
      return {
         endCursor: getSystemLog.systemLogsConnection.pageInfo.endCursor,
         hasNextPage: getSystemLog.systemLogsConnection.pageInfo.hasNextPage,
         items: getSystemLog.systemLogsConnection.items.map(this.convertSystemLogRegisterListItem),
         totalCount: getSystemLog.systemLogsConnection.totalCount,
      };
   }

   private convertSystemLogRegisterListItem(
      getSystemLogItem: GetSystemLogRegister_systemLogsConnection_items
   ): LogRegisterListItem {
      return {
         systemLogId: getSystemLogItem.systemLogId ?? null,
         message: getSystemLogItem.message ?? null,
         severityId: getSystemLogItem.severityName ?? null,
         typeId: getSystemLogItem.typeName ?? null,
         areaId: getSystemLogItem.areaName ?? null,
         logCode: getSystemLogItem.logCode ?? null,
         account: getSystemLogItem.account ?? null,
         createUserDate: getSystemLogItem.createUserDate ?? null,
         callDetail: {
            callDetailId: getSystemLogItem.callDetail?.callDetailId,
            callDestination: getCallExtension(
               { ...getSystemLogItem.callDetail?.destinationExtension },
               getSystemLogItem.callDetail?.callDirectionTypeId,
               getSystemLogItem.callDetail?.destinationNumber ?? null,
               true
            ),
            callDirection: getSystemLogItem.callDetail?.callDirectionTypeId ?? null,
            callSegmentsConnection: getSystemLogItem.callDetail?.callSegmentsConnection ?? null,
            callSource: getCallExtension(
               { ...getSystemLogItem.callDetail?.sourceExtension },
               getSystemLogItem.callDetail?.callDirectionTypeId,
               getSystemLogItem.callDetail?.sourceNumber ?? null
            ),
            destinationExtension: getSystemLogItem.callDetail?.destinationExtension ?? null,
            destinationNumber: getSystemLogItem.callDetail?.destinationNumber ?? null,
            originalDestinationNumber: getSystemLogItem.callDetail?.originalDestinationNumber ?? null,
            phoneNumber: getSystemLogItem.callDetail?.phoneNumber?.phoneNumber ?? null,
            serverName: getSystemLogItem.callDetail?.serverName ?? null,
            sipCallId: getSystemLogItem.callDetail?.sipCallId ?? null,
            startTime: getSystemLogItem.callDetail?.startTime,
            endTime: getSystemLogItem.callDetail?.endTime,
         },
      };
   }
}
